import { Injectable } from '@angular/core';
import { Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';

import { AppSettingsService } from '~/core/services/appsettings.service';
import { AppConfigService } from '~/core/services/appconfig.service';
import { AppSettings } from '~/core/models/appsettings';

@Injectable({
    providedIn: 'root'
})
export class AuthGuard {
    constructor(
        private router: Router,
        private appSettingsService: AppSettingsService,
        private appConfigService: AppConfigService,
    ) { }

    public async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
        const appSettings: AppSettings = await this.appSettingsService.getSettings();
        const routePath = route.routeConfig?.path ?? state.url;

        if (!appSettings.user) {
            console.warn('[AUTH-GUARD] Access blocked: no appSettings.user found', {
                route: routePath,
                redirectTo: '/auth'
            });

            if (!sessionStorage.getItem("_redirect")) {
                sessionStorage.setItem("_redirect", state.url);
            }

            await this.router.navigate(["/auth"]);
            return false;
        }

        const config: any = route.routeConfig;
        let requiredRoles: string[] = Array.isArray(config?.roles) ? config.roles : [];

        // If no route-level roles defined, fall back to the app-level role from config.json
        if (requiredRoles.length === 0) {
            const appRole = this.appConfigService.getConfig('role');
            if (appRole) {
                requiredRoles = [appRole];
            }
        }

        if (requiredRoles.length === 0) {
            console.info('[AUTH-GUARD] Access allowed: no roles configured', {
                route: routePath,
                user: appSettings.user?.username ?? appSettings.user?.preferred_username ?? null
            });
            return true;
        }

        const userRoles: string[] = Array.isArray(appSettings.user?.roles)
            ? appSettings.user.roles
            : (Array.isArray(appSettings.roles) ? appSettings.roles : []);

        const normalizedRequiredRoles = requiredRoles.map((role: string) => String(role).toLowerCase());
        const normalizedUserRoles = userRoles.map((role: string) => String(role).toLowerCase());

        const access = normalizedRequiredRoles.some((requiredRole: string) =>
            normalizedUserRoles.some((userRole: string) =>
                userRole === requiredRole || userRole.includes(requiredRole) || requiredRole.includes(userRole)
            )
        );

        if (!access) {
            console.warn('[AUTH-GUARD] Access denied: role mismatch', {
                route: routePath,
                requiredRoles,
                userRoles,
                user: appSettings.user?.username ?? appSettings.user?.preferred_username ?? null
            });
            await this.router.navigate(["/access"], { state: { type: "page" } });
            return false;
        }

        console.info('[AUTH-GUARD] Access allowed: role matched', {
            route: routePath,
            requiredRoles,
            userRoles,
            user: appSettings.user?.username ?? appSettings.user?.preferred_username ?? null
        });

        return true;
    }
}
